import React, { type ReactNode, useMemo, useCallback, useEffect } from 'react';
import { Field } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';

import { PROJECT_TEMPLATE_DROPDOWN_ID } from '../../../common/constants';
import type { TemplateType } from '../../../common/constants/project';
import type { Template } from '../../../common/constants/steps';
import { useProjectData } from '../../../controllers/import-state';
import messages from './messages';

export type Props = {
	templates: Template[];
};

export type OptionType = {
	value: TemplateType;
	label: ReactNode;
	isDefault?: boolean;
};

export const TemplateSelector = ({ templates }: Props) => {
	const { formatMessage } = useIntl();
	const [{ template }, { setProjectData }] = useProjectData();

	const options: OptionType[] = useMemo(
		() =>
			templates.map((item) => ({
				value: item.key,
				label: (
					<Box xcss={contentWrapperStyles}>
						{item.icon}
						<Box paddingInlineStart="space.100" xcss={titleWrapperStyles}>
							{formatMessage(item.title)}
						</Box>
					</Box>
				),
				isDefault: item.isDefault,
			})),
		[formatMessage, templates],
	);

	const defaultTemplate = useMemo(
		() => options.find(({ isDefault }) => isDefault) || options[0],
		[options],
	);

	useEffect(() => {
		if (!template && defaultTemplate) {
			setProjectData({ template: defaultTemplate.value });
		}
	}, [defaultTemplate, setProjectData, template]);

	const selectedTemplate = useMemo(
		() => options.find(({ value }) => value === template),
		[options, template],
	);

	const onChangeTemplateClick = useCallback(
		(selectedItem: OptionType | null) => {
			if (!selectedItem?.value) {
				return;
			}
			setProjectData({
				template: selectedItem.value,
			});
		},
		[setProjectData],
	);

	return (
		<Field
			label={formatMessage(messages.label)}
			name={formatMessage(messages.label)}
			testId="project-create-importer-upload-flow.ui.project-create-screen.template-selector.field"
		>
			{({ fieldProps }) => (
				<Select<OptionType>
					{...fieldProps}
					inputId={PROJECT_TEMPLATE_DROPDOWN_ID}
					name={PROJECT_TEMPLATE_DROPDOWN_ID}
					options={options}
					value={selectedTemplate}
					onChange={onChangeTemplateClick}
					aria-label={formatMessage(messages.label)}
				/>
			)}
		</Field>
	);
};

const contentWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const titleWrapperStyles = xcss({
	font: token('font.body'),
});
